import {
  createContext,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  manproQueryKeys,
  ownerBidService,
  ownerProjectService,
  type Identifier,
  type ListParams,
  type OwnerProjectBidDto,
  type OwnerProjectDto,
  type OwnerProjectPayload,
  type OwnerProjectStatus,
  type UpdateOwnerProjectPayload,
} from '../../api/manpro';

type OwnerProjectSummary = {
  totalProjects: number;
  totalDraft: number;
  totalOpenForBid: number;
  totalProposalReview: number;
  totalConvertedToManpro: number;
  totalCancelled: number;
  totalCompleted: number;
  totalAccepted: number;
  totalIncomingBids: number;
  totalSubmittedBids: number;
  totalAcceptedBids: number;
  totalRejectedBids: number;
};

type ProjectPagination = {
  current_page: number;
  last_page: number;
  total: number;
  from: number;
  to: number;
};

type OwnerManproContextValue = {
  projects: OwnerProjectDto[];
  projectPagination?: ProjectPagination;
  projectParams: ListParams;
  setProjectParams: (params: ListParams) => void;
  projectSummary: OwnerProjectSummary;
  isLoadingProjects: boolean;
  isFetchingProjects: boolean;
  isLoadingSummary: boolean;
  projectsError: unknown;
  getProjectById: (id: Identifier) => OwnerProjectDto | undefined;
  refreshOwnerProjects: () => Promise<void>;
  createProject: (payload: OwnerProjectPayload) => Promise<OwnerProjectDto>;
  updateProject: (id: Identifier, payload: UpdateOwnerProjectPayload) => Promise<OwnerProjectDto>;
  deleteProject: (id: Identifier) => Promise<unknown>;
  publishProject: (id: Identifier) => Promise<OwnerProjectDto>;
  acceptBid: (projectId: Identifier, bidId: Identifier) => Promise<unknown>;
  rejectBid: (projectId: Identifier, bidId: Identifier) => Promise<OwnerProjectBidDto>;
  isCreatingProject: boolean;
  isUpdatingProject: boolean;
  isDeletingProject: boolean;
  isPublishingProject: boolean;
  isAcceptingBid: boolean;
  isRejectingBid: boolean;
};

type OwnerManproProviderProps = {
  children: ReactNode;
};

const BID_LIST_PARAMS: ListParams = {
  limit: 100,
  offset: 1,
};

const BID_REVIEW_STATUSES: OwnerProjectStatus[] = [
  'open_for_bid',
  'proposal_review',
  'accepted',
  'converted_to_manpro',
];

const OwnerManproContext = createContext<OwnerManproContextValue | null>(null);

const getBidAmount = (bid: OwnerProjectBidDto) => bid.amount ?? bid.offered_price ?? 0;

// Menyamakan status lama dan baru agar page Owner cukup membaca satu istilah status.
const normalizeStatus = (status?: string) => {
  if (status === 'pending') return 'submitted';
  if (status === 'denied') return 'rejected';
  return status;
};

const getApiErrorMessage = (error: unknown, fallback: string) => {
  const response = (error as { response?: { data?: { errors?: unknown; message?: string } } })?.response;
  const errors = response?.data?.errors;

  if (Array.isArray(errors) && errors.length > 0) {
    const firstError = errors[0] as { message?: string };
    return firstError.message ?? fallback;
  }

  return response?.data?.message ?? fallback;
};

export const OwnerManproProvider = ({ children }: OwnerManproProviderProps) => {
  const queryClient = useQueryClient();

  const [projectParams, setProjectParams] = useState<ListParams>({
    limit: 10,
    offset: 1,
  });

  // Source utama data Owner Project dari API MANPRO; menggantikan flow lama berbasis localStorage.
  const projectsQuery = useQuery({
    queryKey: manproQueryKeys.ownerProjects(projectParams),
    queryFn: () => ownerProjectService.getOwnerProjects(projectParams),
    refetchInterval: 30000,
  });

  const projectsSummaryQuery = useQuery({
    queryKey: manproQueryKeys.ownerProjectsSummary(),
    queryFn: () => ownerProjectService.getOwnerProjectSummary(),
    refetchInterval: 30000,
  });

  const projects = useMemo(
    () => projectsQuery.data?.data ?? [],
    [projectsQuery.data?.data],
  );

  const projectPagination = useMemo<ProjectPagination | undefined>(() => {
    if (!projectsQuery.data) return undefined;
    return {
      current_page: projectsQuery.data.current_page || 1,
      last_page: projectsQuery.data.last_page || 1,
      total: projectsQuery.data.total || 0,
      from: projectsQuery.data.from || 0,
      to: projectsQuery.data.to || 0,
    };
  }, [projectsQuery.data]);

  const bidEligibleProjects = useMemo(
    () => projects.filter((project) => BID_REVIEW_STATUSES.includes(project.status)),
    [projects],
  );

  const bidsSummaryQuery = useQuery({
    queryKey: manproQueryKeys.ownerBidsSummary(),
    queryFn: () => ownerBidService.getBidsSummary(),
    refetchInterval: 30000,
  });

  // Summary dipakai bersama oleh dashboard Owner, badge header/sidebar, Proyek Saya, dan Penawaran Masuk.
  const projectSummary = useMemo<OwnerProjectSummary>(() => {
    const pSummary = projectsSummaryQuery.data || {};
    const bSummary = bidsSummaryQuery.data || { total: 0, pending: 0, accepted: 0, rejected: 0 };

    return {
      totalProjects: pSummary.total_projects ?? 0,
      totalDraft: pSummary.draft ?? 0,
      totalOpenForBid: pSummary.open_for_bid ?? 0,
      totalProposalReview: pSummary.proposal_review ?? 0,
      totalConvertedToManpro: pSummary.converted_to_manpro ?? 0,
      totalCancelled: pSummary.cancelled ?? 0,
      totalCompleted: pSummary.completed ?? 0,
      totalAccepted: pSummary.accepted ?? 0,
      totalIncomingBids: bSummary.total,
      totalSubmittedBids: bSummary.pending,
      totalAcceptedBids: bSummary.accepted,
      totalRejectedBids: bSummary.rejected,
    };
  }, [projectsSummaryQuery.data, bidsSummaryQuery.data]);

  // Satu aksi Owner bisa mengubah data lintas flow: project, bid, opportunity Provider, dan monitoring.
  const invalidateOwnerProjectFlow = async (projectId?: Identifier) => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['manpro', 'owner', 'projects'] }),
      projectId
        ? queryClient.invalidateQueries({ queryKey: manproQueryKeys.ownerProject(projectId) })
        : Promise.resolve(),
      projectId
        ? queryClient.invalidateQueries({ queryKey: ['manpro', 'owner', 'projects', String(projectId), 'bids'] })
        : Promise.resolve(),
      projectId
        ? queryClient.invalidateQueries({ queryKey: manproQueryKeys.ownerMonitoring(projectId) })
        : Promise.resolve(),
      queryClient.invalidateQueries({ queryKey: ['manpro', 'provider', 'opportunities'] }),
      queryClient.invalidateQueries({ queryKey: ['manpro', 'provider', 'bids'] }),
    ]);
  };

  const createProjectMutation = useMutation({
    mutationFn: (payload: OwnerProjectPayload) => ownerProjectService.createOwnerProject(payload),
    onSuccess: async () => {
      await invalidateOwnerProjectFlow();
      toast.success('Draft proyek berhasil dibuat.');
    },
    onError: (error) => {
      const errorsArray = (error as any)?.response?.data?.errors;
      if (Array.isArray(errorsArray) && errorsArray.length > 0) return; // Disembunyikan agar ditangani form field-level
      toast.error('Gagal membuat draft proyek.');
    },
  });

  const updateProjectMutation = useMutation({
    mutationFn: ({ id, payload }: { id: Identifier; payload: UpdateOwnerProjectPayload }) =>
      ownerProjectService.updateOwnerProject(id, payload),
    onSuccess: async (_data, variables) => {
      await invalidateOwnerProjectFlow(variables.id);
      toast.success('Draft proyek berhasil diperbarui.');
    },
    onError: (error) => {
      const errorsArray = (error as any)?.response?.data?.errors;
      if (Array.isArray(errorsArray) && errorsArray.length > 0) return; // Disembunyikan agar ditangani form field-level
      
      const message = (error as any)?.response?.data?.message ?? 'Gagal memperbarui draft proyek.';
      toast.error(message);
    },
  });

  const deleteProjectMutation = useMutation({
    mutationFn: (id: Identifier) => ownerProjectService.deleteOwnerProject(id),
    onSuccess: async () => {
      await invalidateOwnerProjectFlow();
      toast.success('Draft proyek berhasil dihapus.');
    },
    onError: () => {
      toast.error('Gagal menghapus draft proyek.');
    },
  });

  const publishProjectMutation = useMutation({
    mutationFn: (id: Identifier) => ownerProjectService.publishOwnerProject(id),
    onSuccess: async (_data, id) => {
      await invalidateOwnerProjectFlow(id);
      toast.success('Proyek berhasil dipublikasikan dan masuk opportunity provider.');
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error, 'Gagal mempublikasikan proyek.'));
    },
  });

  const acceptBidMutation = useMutation({
    mutationFn: ({ projectId, bidId }: { projectId: Identifier; bidId: Identifier }) =>
      ownerBidService.acceptOwnerProjectBid(projectId, bidId),
    onSuccess: async (_data, variables) => {
      // Accept bid di backend sekaligus membuat project MANPRO, jadi query monitoring wajib ikut refresh.
      await invalidateOwnerProjectFlow(variables.projectId);
      toast.success('Provider diterima. Proyek masuk Monitoring Owner.');
    },
    onError: () => {
      toast.error('Gagal menerima penawaran provider.');
    },
  });

  const rejectBidMutation = useMutation({
    mutationFn: ({ projectId, bidId }: { projectId: Identifier; bidId: Identifier }) =>
      ownerBidService.rejectOwnerProjectBid(projectId, bidId),
    onSuccess: async (_data, variables) => {
      await invalidateOwnerProjectFlow(variables.projectId);
      toast.success('Penawaran provider ditolak.');
    },
    onError: () => {
      toast.error('Gagal menolak penawaran provider.');
    },
  });

  const value = useMemo<OwnerManproContextValue>(
    () => ({
      projects,
      projectPagination,
      projectParams,
      setProjectParams,
      projectSummary,
      isLoadingProjects: projectsQuery.isLoading,
      isFetchingProjects: projectsQuery.isFetching,
      isLoadingSummary: bidsSummaryQuery.isLoading,
      projectsError: projectsQuery.error,
      getProjectById: (id) => projects.find((project) => project.id === String(id)),
      refreshOwnerProjects: async () => {
        await invalidateOwnerProjectFlow();
      },
      createProject: (payload) => createProjectMutation.mutateAsync(payload),
      updateProject: (id, payload) => updateProjectMutation.mutateAsync({ id, payload }),
      deleteProject: (id) => deleteProjectMutation.mutateAsync(id),
      publishProject: (id) => publishProjectMutation.mutateAsync(id),
      acceptBid: (projectId, bidId) => acceptBidMutation.mutateAsync({ projectId, bidId }),
      rejectBid: (projectId, bidId) => rejectBidMutation.mutateAsync({ projectId, bidId }),
      isCreatingProject: createProjectMutation.isPending,
      isUpdatingProject: updateProjectMutation.isPending,
      isDeletingProject: deleteProjectMutation.isPending,
      isPublishingProject: publishProjectMutation.isPending,
      isAcceptingBid: acceptBidMutation.isPending,
      isRejectingBid: rejectBidMutation.isPending,
    }),
    [
      acceptBidMutation,
      bidsSummaryQuery,
      createProjectMutation,
      deleteProjectMutation,
      projectPagination,
      projectParams,
      projectSummary,
      projects,
      projectsQuery.error,
      projectsQuery.isFetching,
      projectsQuery.isLoading,
      bidsSummaryQuery.isLoading,
      publishProjectMutation,
      rejectBidMutation,
      updateProjectMutation,
    ],
  );

  return (
    <OwnerManproContext.Provider value={value}>
      {children}
    </OwnerManproContext.Provider>
  );
};

export const useOwnerManpro = () => {
  const context = useContext(OwnerManproContext);

  if (!context) {
    throw new Error('useOwnerManpro must be used within OwnerManproProvider');
  }

  return context;
};
